import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import RegionalService from '../services/RegionalService';
import areaService from '../services/AreaService';
import materiaService from '../services/MateriaService';
import tipoAsistenteService from '../services/TipoAsistenteService';
import ProcesosMenu from './common/ProcesosMenu';
import './NewAssistant.css';
import {
  canAccessGptClassic,
  isAdminArea,
  isAdminUser as isAdminUserRole,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateAdminMenuItem, navigateProfessorMenuItem, NEW_GPT_MENU_ITEM } from './sidebar/menuConfig';
import gptClassicModels from '../assets/gptClassicModels.json';
import { findAssistantTypeById, isInstitutionalAssistantType } from '../utils/asistenteTypes';

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';
const DEFAULT_GPT_CLASSIC_MODEL = gptClassicModels[0]?.nombre || '';
const EXTRA_INSTRUCTIONS_MARKER = 'ACLARACIONES EXTRAS QUE DEBES CUMPLIR:';
const POST_RESPONSE_MARKER = '## PASO 3';

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const NewGptAssistant = ({ editAssistantId = null } = {}) => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdminAreaUser = Boolean(user) && isAdminArea(user);
  const isAdminUser = Boolean(user) && isAdminUserRole(user);
  const isEditMode = Boolean(editAssistantId);
  const featureFlags = RegionalService.getFeatureFlags();
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const isGptClassicAllowed = canAccessGptClassic(user, isGptClassicEnabled);
  
  // Estados del componente
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAssistant, setIsLoadingAssistant] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [formData, setFormData] = useState({
    nombre: '',
    gptClasicoDescripcion: '',
    promptExtra: '',
    instruccionesInstitucionales: '',
    gptClasicoFraseIniciarConversacion: '',
    gptClasicoModelo: DEFAULT_GPT_CLASSIC_MODEL,
    idRegional: '',
    idArea: '',
    idMateria: '',
    idTipoAsistente: '',
    archivosPdf: [],
    publico: true,
    passwordAsistente: '',
    gptClasicoBusquedaEnLaWeb: false,
    gptClasicoLienzo: true,
    gptClasicoGeneracionDeImagen: true,
    gptClasicoInterpreteCodigoYAnalisisDatos: true
  });
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [materiaFallback, setMateriaFallback] = useState(null);
  const [availableAreas, setAvailableAreas] = useState(() => {
    const storedAreas = RegionalService.getAreas();
    return Array.isArray(storedAreas) ? storedAreas : [];
  });
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState('');
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);
  const [tiposError, setTiposError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loadedAssistantId, setLoadedAssistantId] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);

  const availableModels = useMemo(() => {
    const currentModel = formData.gptClasicoModelo?.trim();
    if (!currentModel) {
      return gptClassicModels;
    }

    const exists = gptClassicModels.some((model) => model?.nombre === currentModel);
    if (exists) {
      return gptClassicModels;
    }

    return [
      {
        nombre: currentModel,
        caracteristicas: ['Modelo actual guardado en este GPT. No figura en el JSON editable.'],
        legacy: true
      },
      ...gptClassicModels
    ];
  }, [formData.gptClasicoModelo]);

  const selectedModelConfig = useMemo(() => {
    const currentModel = formData.gptClasicoModelo?.trim();
    if (!currentModel) {
      return null;
    }

    return availableModels.find((model) => model?.nombre === currentModel) || null;
  }, [availableModels, formData.gptClasicoModelo]);
  const selectedTipoAsistente = useMemo(
    () => findAssistantTypeById(tiposAsistente, formData.idTipoAsistente),
    [tiposAsistente, formData.idTipoAsistente]
  );
  const isInstitutionalTipoSeleccionado = useMemo(
    () => isInstitutionalAssistantType(selectedTipoAsistente || { id: formData.idTipoAsistente }),
    [selectedTipoAsistente, formData.idTipoAsistente]
  );
  const materiaEsObligatoria = !(isInstitutionalTipoSeleccionado && (isAdminUser || isAdminAreaUser));

  const resolveAdminAreaInstitutionalAreaId = (currentAreaId = '') => {
    if (!isAdminAreaUser || !isInstitutionalTipoSeleccionado || formData.idMateria) {
      return currentAreaId || '';
    }

    const normalizedCurrent = String(currentAreaId || '').trim();
    if (normalizedCurrent) {
      const currentExists = availableAreas.some((area) => (area.id || area.Id) === normalizedCurrent);
      if (currentExists) {
        return normalizedCurrent;
      }
    }

    if (availableAreas.length === 1) {
      return availableAreas[0]?.id || availableAreas[0]?.Id || '';
    }

    return '';
  };

  // Validar permisos al montar el componente
  useEffect(() => {
    if (authLoading) return;
    validateUserPermissions();
  }, [user, authLoading, isGptClassicEnabled]);

  const extractPromptExtra = (instructions) => {
    if (!instructions) return '';
    const indexes = [];
    let searchFrom = 0;

    while (searchFrom < instructions.length) {
      const idx = instructions.indexOf(EXTRA_INSTRUCTIONS_MARKER, searchFrom);
      if (idx === -1) break;
      indexes.push(idx);
      searchFrom = idx + EXTRA_INSTRUCTIONS_MARKER.length;
    }

    for (let i = indexes.length - 1; i >= 0; i -= 1) {
      let candidate = instructions.slice(indexes[i] + EXTRA_INSTRUCTIONS_MARKER.length);
      const suffixIdx = candidate.indexOf(POST_RESPONSE_MARKER);
      if (suffixIdx !== -1) {
        candidate = candidate.slice(0, suffixIdx);
      }

      const normalized = candidate.trim();
      if (normalized) {
        return normalized;
      }
    }

    const suffixIdx = instructions.indexOf(POST_RESPONSE_MARKER);
    const rawValue = (suffixIdx === -1 ? instructions : instructions.slice(0, suffixIdx)).trim();
    return rawValue;
  };

  const validateUserPermissions = () => {
    try {
      if (!isGptClassicAllowed) {
        setHasPermission(false);
        setAlert({
          show: true,
          title: 'Funcionalidad deshabilitada',
          message: 'El feature flag de AsistentesClasicos GPT no está habilitado para esta regional.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Verificar si hay usuario autenticado
      if (!user) {
        setAlert({
          show: true,
          title: 'Sesión requerida',
          message: 'Debes iniciar sesión para acceder a esta funcionalidad',
          type: 'error'
        });
        setHasPermission(false);
        setIsLoading(false);
        
        // Redirigir al login después de un breve retraso
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      setHasPermission(true);
      
    } catch (error) {
      console.error('Error al validar permisos:', error);
      setHasPermission(false);
      setAlert({
        show: true,
        title: 'Error de permisos',
        message: 'Error al validar permisos. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isGptClassicAllowed) return;
    const activeRegionalId = RegionalService.getActiveRegionalId();
    setFormData((prev) => ({ ...prev, idRegional: activeRegionalId || prev.idRegional }));

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        let list = [];
        if (isAdminAreaUser) {
          list = activeRegionalId
            ? await materiaService.getMisMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMisMaterias();
        } else if (isAdminUser) {
          list = activeRegionalId
            ? await materiaService.getMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMaterias();
        } else {
          list = activeRegionalId
            ? await materiaService.getMisMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMisMaterias();
        }
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar las materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    const fetchTipos = async () => {
      setTiposLoading(true);
      setTiposError('');
      try {
        const list = await tipoAsistenteService.getTiposActivos();
        setTiposAsistente(Array.isArray(list) ? list : []);
      } catch (error) {
        setTiposError(error.message || 'No pudimos cargar los tipos de asistente.');
      } finally {
        setTiposLoading(false);
      }
    };

    fetchMaterias();
    fetchTipos();
  }, [isGptClassicAllowed]);

  useEffect(() => {
    if (!isGptClassicAllowed || !isAdminAreaUser) {
      setAreasLoading(false);
      setAreasError('');
      return;
    }

    const regionalId = formData.idRegional || RegionalService.getActiveRegionalId();
    if (!regionalId) {
      setAvailableAreas([]);
      setAreasError('No pudimos determinar el regional activo para cargar tus áreas.');
      return;
    }

    let cancelled = false;

    const fetchAreas = async () => {
      setAreasLoading(true);
      setAreasError('');
      try {
        const list = await areaService.getAreasActivas(regionalId);
        if (cancelled) return;

        const nextAreas = Array.isArray(list) ? list : [];
        setAvailableAreas(nextAreas);
        RegionalService.setRegionalData({
          regionales: RegionalService.getRegionales(),
          areas: nextAreas,
          materias: RegionalService.getMaterias()
        });
      } catch (error) {
        if (cancelled) return;
        setAreasError(error.message || 'No pudimos cargar las áreas asignadas.');
        setAvailableAreas([]);
      } finally {
        if (!cancelled) {
          setAreasLoading(false);
        }
      }
    };

    fetchAreas();

    return () => {
      cancelled = true;
    };
  }, [formData.idRegional, isAdminAreaUser, isGptClassicAllowed]);

  useEffect(() => {
    if (!isEditMode || !editAssistantId || !isGptClassicAllowed) return;
    if (loadedAssistantId === editAssistantId) return;

    const loadAssistant = async () => {
      setIsLoadingAssistant(true);
      try {
        const response = await AsistentesClasicosService.getAsistente(editAssistantId);
        const asistente =
          response?.asistente ||
          response?.Asistente ||
          response?.data?.asistente ||
          response?.data?.Asistente ||
          response;

        if (!asistente) {
          throw new Error('No se encontró el GPT');
        }

        const resolvedNombre =
          asistente.gptClasicoNombre ||
          asistente.GPTClasicoNombre ||
          asistente.nombre ||
          asistente.Nombre ||
          '';

        const resolvedDescripcion =
          asistente.gptClasicoDescripcion ||
          asistente.GPTClasicoDescripcion ||
          asistente.descripcion ||
          asistente.Descripcion ||
          '';

        const resolvedInstrucciones =
          asistente.gptClasicoInstrucciones ||
          asistente.GPTClasicoInstrucciones ||
          '';

        const resolvedMateriaId = asistente.idMateria || asistente.IdMateria || '';
        const resolvedMateriaNombre = asistente.nombreMateria || asistente.NombreMateria || '';
        const resolvedMateriaArea = asistente.idArea || asistente.IdArea || '';
        const resolvedMateriaRegional = asistente.idRegional || asistente.IdRegional || '';

        setFormData((prev) => ({
          ...prev,
          nombre: resolvedNombre,
          gptClasicoDescripcion: resolvedDescripcion,
          promptExtra:
            asistente.promptExtra ||
            asistente.PromptExtra ||
            extractPromptExtra(resolvedInstrucciones),
          instruccionesInstitucionales:
            asistente.instruccionesInstitucionales ||
            asistente.InstruccionesInstitucionales ||
            '',
          gptClasicoFraseIniciarConversacion:
            asistente.gptClasicoFraseIniciarConversacion ||
            asistente.GPTClasicoFraseIniciarConversacion ||
            '',
          gptClasicoModelo:
            asistente.gptClasicoModelo ||
            asistente.GPTClasicoModelo ||
            prev.gptClasicoModelo,
          idRegional: resolvedMateriaRegional || prev.idRegional,
          idArea: resolvedMateriaArea || '',
          idMateria: resolvedMateriaId,
          idTipoAsistente: asistente.idTipoAsistente || asistente.IdTipoAsistente || prev.idTipoAsistente,
          publico: asistente.publico ?? asistente.Publico ?? true,
          passwordAsistente: asistente.passwordAsistente || asistente.PasswordAsistente || '',
          gptClasicoBusquedaEnLaWeb:
            asistente.gptClasicoBusquedaEnLaWeb ??
            asistente.GPTClasicoBusquedaEnLaWeb ??
            prev.gptClasicoBusquedaEnLaWeb,
          gptClasicoLienzo:
            asistente.gptClasicoLienzo ??
            asistente.GPTClasicoLienzo ??
            prev.gptClasicoLienzo,
          gptClasicoGeneracionDeImagen:
            asistente.gptClasicoGeneracionDeImagen ??
            asistente.GPTClasicoGeneracionDeImagen ??
            prev.gptClasicoGeneracionDeImagen,
          gptClasicoInterpreteCodigoYAnalisisDatos:
            asistente.gptClasicoInterpreteCodigoYAnalisisDatos ??
            asistente.GPTClasicoInterpreteCodigoYAnalisisDatos ??
            prev.gptClasicoInterpreteCodigoYAnalisisDatos
        }));

        if (resolvedMateriaId && resolvedMateriaNombre) {
          setMateriaFallback({
            Id: resolvedMateriaId,
            Nombre: resolvedMateriaNombre,
            IdArea: resolvedMateriaArea || null,
            IdRegional: resolvedMateriaRegional || null
          });
        } else {
          setMateriaFallback(null);
        }

        const archivos = asistente.archivos || asistente.Archivos || [];
        setExistingFiles(Array.isArray(archivos) ? archivos : []);

        setLoadedAssistantId(editAssistantId);
      } catch (error) {
        setAlert({
          show: true,
          title: 'Error al cargar el GPT',
          message: getFriendlyApiErrorMessage(error, 'No pudimos obtener los datos del GPT Clásico. Inténtalo nuevamente.'),
          type: 'error'
        });
      } finally {
        setIsLoadingAssistant(false);
      }
    };

    loadAssistant();
  }, [isEditMode, editAssistantId, isGptClassicAllowed, loadedAssistantId]);

  useEffect(() => {
    if (!materiaFallback || !materiaFallback.Id) return;
    const exists = materias.some((m) => (m.id || m.Id) === materiaFallback.Id);
    if (!exists) {
      setMaterias((prev) => [materiaFallback, ...prev]);
    }
  }, [materiaFallback, materias]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTipoAsistenteChange = (value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        idTipoAsistente: value
      };

      const nextIsInstitutional = isInstitutionalAssistantType(findAssistantTypeById(tiposAsistente, value) || { id: value });
      if (isAdminAreaUser && nextIsInstitutional && !prev.idMateria) {
        next.idArea = resolveAdminAreaInstitutionalAreaId(prev.idArea);
      }

      return next;
    });
  };

  const handleModelChange = (value) => {
    handleInputChange('gptClasicoModelo', value);
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    const idRegional = selected?.idRegional || selected?.IdRegional || formData.idRegional || '';
    setFormData((prev) => ({
      ...prev,
      idMateria: value,
      idArea: value ? idArea : resolveAdminAreaInstitutionalAreaId(prev.idArea),
      idRegional
    }));
  };

  const handleAreaChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      idArea: value
    }));
  };

  useEffect(() => {
    if (!isAdminAreaUser || !isInstitutionalTipoSeleccionado || formData.idMateria) {
      return;
    }

    const normalizedCurrent = String(formData.idArea || '').trim();
    let resolvedAreaId = normalizedCurrent;

    if (normalizedCurrent) {
      const currentExists = availableAreas.some((area) => (area.id || area.Id) === normalizedCurrent);
      if (!currentExists) {
        resolvedAreaId = '';
      }
    }

    if (!resolvedAreaId && availableAreas.length === 1) {
      resolvedAreaId = availableAreas[0]?.id || availableAreas[0]?.Id || '';
    }

    if (resolvedAreaId !== formData.idArea) {
      setFormData((prev) => ({
        ...prev,
        idArea: resolvedAreaId
      }));
    }
  }, [
    availableAreas,
    formData.idArea,
    formData.idMateria,
    isAdminAreaUser,
    isInstitutionalTipoSeleccionado
  ]);

  // Manejar drag and drop de archivos
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (newFiles) => {
    // Filtrar solo archivos PDF
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF',
        type: 'warning'
      });
    }

    setFormData(prev => {
      const currentFiles = prev.archivosPdf;
      const totalFiles = [...currentFiles, ...pdfFiles];
      
      // Validar máximo 20 archivos
      if (totalFiles.length > 20) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'No se pueden subir más de 20 archivos PDF',
          type: 'error'
        });
        return prev; // No actualizar si excedel límite
      }
      
      return {
        ...prev,
        archivosPdf: totalFiles
      };
    });
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      archivosPdf: prev.archivosPdf.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre.trim()) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'El nombre del asistente es obligatorio',
        type: 'error'
      });
      return;
    }

    if (materiaEsObligatoria && materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && materias.length === 0) {
      setAlert({
        show: true,
        title: 'Sin materias asignadas',
        message: 'No tienes materias asignadas para asociar al asistente.',
        type: 'warning'
      });
      return;
    }

    if (materiaEsObligatoria && !formData.idMateria) {
      setAlert({
        show: true,
        title: 'Materia requerida',
        message: 'Selecciona una materia asignada para el asistente.',
        type: 'warning'
      });
      return;
    }

    if (isInstitutionalTipoSeleccionado && !formData.instruccionesInstitucionales.trim()) {
      setAlert({
        show: true,
        title: 'Instrucciones requeridas',
        message: 'Debes ingresar las instrucciones del asistente institucional.',
        type: 'warning'
      });
      return;
    }

    if (isAdminAreaUser && isInstitutionalTipoSeleccionado && !formData.idMateria) {
      if (areasLoading) {
        setAlert({
          show: true,
          title: 'Áreas cargando',
          message: 'Esperá a que carguen tus áreas para continuar.',
          type: 'warning'
        });
        return;
      }

      if (availableAreas.length === 0) {
        setAlert({
          show: true,
          title: 'Sin áreas asignadas',
          message: 'No tienes áreas asignadas para asociar al asistente institucional.',
          type: 'warning'
        });
        return;
      }

      if (!formData.idArea) {
        setAlert({
          show: true,
          title: 'Área requerida',
          message: 'Selecciona el área para el asistente institucional.',
          type: 'warning'
        });
        return;
      }
    }

    if (!isEditMode && formData.archivosPdf.length === 0) {
      setAlert({
        show: true,
        title: 'Archivos requeridos',
        message: 'Debes subir al menos un archivo PDF',
        type: 'error'
      });
      return;
    }

    if (formData.archivosPdf.length > 20) {
      setAlert({
        show: true,
        title: 'Límite de archivos',
        message: 'No puedes subir más de 20 archivos PDF',
        type: 'error'
      });
      return;
    }
    if (!formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el GPT es privado.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mostrar mensaje inicial
      setAlert({
        show: true,
        title: isEditMode ? 'Actualizando GPT Clásico' : 'Creando GPT Clásico',
        message: isEditMode
          ? 'Actualizando GPT Clásico... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.'
          : 'Creando GPT Clásico... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.',
        type: 'info'
      });
      
      // Crear FormData para envío multipart
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('gptClasicoNombre', formData.nombre);
      const normalizedDescription = formData.gptClasicoDescripcion.trim();
      if (isEditMode || normalizedDescription) {
        formDataToSend.append('gptClasicoDescripcion', normalizedDescription);
      }
      if (formData.idRegional) {
        formDataToSend.append('idRegional', formData.idRegional);
      }
      if (formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (formData.idTipoAsistente) {
        formDataToSend.append('idTipoAsistente', formData.idTipoAsistente);
      }

      if (formData.archivosPdf.length > 0) {
        setAlert({
          show: true,
          title: isEditMode ? 'Actualizando GPT Clásico' : 'Creando GPT Clásico',
          message: 'Subiendo archivos... Este proceso puede demorar unos minutos si los PDFs son pesados.',
          type: 'info'
        });

        const uploadedTempFiles = await AsistentesClasicosService.uploadFilesInChunks(formData.archivosPdf);
        if (uploadedTempFiles.length > 0) {
          formDataToSend.append('uploadedTempFilesJson', JSON.stringify(uploadedTempFiles));
        }
      }

      const normalizedPromptExtra = formData.promptExtra.trim();
      if (isEditMode || normalizedPromptExtra) {
        formDataToSend.append('promptExtra', normalizedPromptExtra);
      }
      if (isInstitutionalTipoSeleccionado && formData.instruccionesInstitucionales.trim()) {
        formDataToSend.append('instruccionesInstitucionales', formData.instruccionesInstitucionales.trim());
      }
      const normalizedConversationStarter = formData.gptClasicoFraseIniciarConversacion.trim();
      if (isEditMode || normalizedConversationStarter) {
        formDataToSend.append('gptClasicoFraseIniciarConversacion', normalizedConversationStarter);
      }
      if (formData.gptClasicoModelo) {
        formDataToSend.append('gptClasicoModelo', formData.gptClasicoModelo);
      }
      formDataToSend.append('gptClasicoBusquedaEnLaWeb', String(formData.gptClasicoBusquedaEnLaWeb));
      formDataToSend.append('gptClasicoLienzo', String(formData.gptClasicoLienzo));
      formDataToSend.append('gptClasicoGeneracionDeImagen', String(formData.gptClasicoGeneracionDeImagen));
      formDataToSend.append('gptClasicoInterpreteCodigoYAnalisisDatos', String(formData.gptClasicoInterpreteCodigoYAnalisisDatos));
      formDataToSend.append('publico', String(formData.publico));
      formDataToSend.append('conPassword', String(!formData.publico));
      if (!formData.publico && formData.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
      }

      // Hacer la petición POST/PUT
      if (isEditMode) {
        await AsistentesClasicosService.actualizarAsistente(editAssistantId, formDataToSend);
      } else {
        await AsistentesClasicosService.crearAsistente(formDataToSend);
      }
      
      setAlert({
        show: true,
        title: isEditMode ? 'GPT Clásico actualizado' : 'GPT Clásico creado',
        message: isEditMode ? 'GPT Clásico actualizado correctamente.' : 'GPT Clásico creado exitosamente',
        type: 'success'
      });
      
      // Limpiar formulario después del éxito
      setTimeout(() => {
        if (!isEditMode) {
          setFormData({
            nombre: '',
            gptClasicoDescripcion: '',
            promptExtra: '',
            instruccionesInstitucionales: '',
            gptClasicoFraseIniciarConversacion: '',
            gptClasicoModelo: DEFAULT_GPT_CLASSIC_MODEL,
            idRegional: RegionalService.getActiveRegionalId() || '',
            idArea: '',
            idMateria: '',
            idTipoAsistente: '',
            archivosPdf: [],
            publico: true,
            passwordAsistente: '',
            gptClasicoBusquedaEnLaWeb: false,
            gptClasicoLienzo: true,
            gptClasicoGeneracionDeImagen: true,
            gptClasicoInterpreteCodigoYAnalisisDatos: true
          });
        }
        navigate('/gpt-assistants');
      }, 1500);
      
    } catch (error) {
      console.error(isEditMode ? '❌ Error al actualizar asistente:' : '❌ Error al crear asistente:', error);
      setAlert({
        show: true,
        title: isEditMode ? 'Error al actualizar asistente' : 'Error al crear asistente',
        message: getFriendlyApiErrorMessage(
          error,
          isEditMode ? 'No pudimos actualizar el asistente. Inténtalo nuevamente.' : 'No pudimos crear el asistente. Inténtalo nuevamente.'
        ),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleAdminNavigate = (itemId) => {
    navigateAdminMenuItem(navigate, itemId);
  };

  const handleGoBack = () => {
    navigate(isEditMode ? '/gpt-assistants' : '/dashboard');
  };

  const handleLogout = () => {
    logout();
  };

  const handleProfessorMenuSelect = (item) => {
    navigateProfessorMenuItem(navigate, item.id);
  };

  // Mostrar loading mientras se validan permisos
  if (authLoading || isLoading || isLoadingAssistant) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message={isLoadingAssistant ? 'Cargando GPT...' : 'Validando permisos...'} />
        </div>
      </div>
    );
  }

  // Redirigir al dashboard si no tiene permisos (mostrar alerta allí)
  if (!hasPermission) {
    navigate('/dashboard', { replace: true, state: { deniedCreateAssistant: true } });
    return null;
  }

  // Mostrar formulario de creación si tiene permisos
  return (
    <div className={`new-dashboard ${isAdminUser ? 'admin-mode' : ''}`}>
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdminUser ? (
            <AdminRoleMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              activeItemId="asistentes-gpt"
              onSelect={(item) => handleAdminNavigate(item.id)}
            />
          ) : (
            <ProfessorMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              extraItems={[NEW_GPT_MENU_ITEM]}
              activeItemId="nuevo-gpt"
              onSelect={handleProfessorMenuSelect}
            />
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || user?.roleName || user?.nombreRol || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              {isEditMode ? 'Editar GPT Clásico' : 'Crear GPT Clásico'}
            </h1>
            <p className="page-subtitle">
              {isEditMode
                ? 'Actualiza la información y archivos del GPT Clásico'
                : 'Sube archivos PDF para crear un GPT Clásico personalizado'}
            </p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          <div className="new-assistant-content">
            <form onSubmit={handleSubmit} className="assistant-form">
              <div className="assistant-form-grid">
                <div className="assistant-form-panel">
              {/* Información básica */}
              <div className="form-section basic-info-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Información del GPT Clásico</h3>
                    <p className="section-description">Define el nombre y características básicas de tu GPT Clásico</p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="nombre" className="form-label">
                    <i className="fas fa-tag"></i>
                    Nombre del GPT Clásico *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    className="form-input enhanced"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Ej: GPT Clásico de Lógica y Estructuras Discretas"
                    required
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-lightbulb"></i>
                    Escoge un nombre descriptivo que refleje el área de conocimiento
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="gptClasicoDescripcion" className="form-label">
                    <i className="fas fa-align-left"></i>
                    Descripción del GPT Clásico
                  </label>
                  <textarea
                    id="gptClasicoDescripcion"
                    className="form-input enhanced"
                    rows="3"
                    value={formData.gptClasicoDescripcion}
                    onChange={(e) => handleInputChange('gptClasicoDescripcion', e.target.value)}
                    placeholder="Ej: Asistente especializado en Lógica y Estructuras Discretas para alumnos de primer año"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Breve descripción que verán los usuarios del GPT Clásico
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="idTipoAsistente" className="form-label">
                    <i className="fas fa-layer-group"></i>
                    Tipo de asistente
                  </label>
                  <select
                    id="idTipoAsistente"
                    className="form-select enhanced"
                    value={formData.idTipoAsistente}
                    onChange={(e) => handleTipoAsistenteChange(e.target.value)}
                    disabled={isSubmitting || tiposLoading}
                  >
                    <option value="">Selecciona un tipo</option>
                    {tiposAsistente.map((tipo) => {
                      const id = tipo.id || tipo.Id;
                      const nombre = tipo.nombre || tipo.Nombre || 'Tipo';
                      return (
                        <option key={id} value={id}>
                          {nombre}
                        </option>
                      );
                    })}
                  </select>
                  {tiposLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando tipos de asistente...
                    </div>
                  )}
                  {tiposError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {tiposError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="gptClasicoModelo" className="form-label">
                    <i className="fas fa-microchip"></i>
                    Modelo GPT Clásico
                  </label>
                  <select
                    id="gptClasicoModelo"
                    className="form-select enhanced"
                    value={formData.gptClasicoModelo}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={isSubmitting}
                    required
                  >
                    {availableModels.map((modelo) => (
                      <option key={modelo.nombre} value={modelo.nombre}>
                        {modelo.nombre}{modelo.legacy ? ' (actual)' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedModelConfig && Array.isArray(selectedModelConfig.caracteristicas) && selectedModelConfig.caracteristicas.length > 0 && (
                    <div className="model-characteristics-card">
                      <div className="model-characteristics-header">
                        <i className="fas fa-circle-info"></i>
                        <span>{selectedModelConfig.nombre}</span>
                      </div>
                      <ul className="model-characteristics-list">
                        {selectedModelConfig.caracteristicas.map((caracteristica, index) => (
                          <li key={`${selectedModelConfig.nombre}-${index}`}>{caracteristica}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="idMateria" className="form-label">
                    <i className="fas fa-book"></i>
                    {materiaEsObligatoria ? 'Materia asignada *' : 'Materia asignada (opcional)'}
                  </label>
                  <select
                    id="idMateria"
                    className="form-select enhanced"
                    value={formData.idMateria}
                    onChange={(e) => handleMateriaChange(e.target.value)}
                    disabled={isSubmitting || materiasLoading}
                    required={materiaEsObligatoria}
                  >
                    <option value="">{materiaEsObligatoria ? 'Selecciona una materia' : 'Sin materia'}</option>
                    {materias.map((materia) => {
                      const id = materia.id || materia.Id;
                      const nombre = materia.nombre || materia.Nombre || '';
                      const codigo = materia.codigo || materia.Codigo || '';
                      const label = nombre || codigo || 'Materia';
                      return (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {materiasLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando materias asignadas...
                    </div>
                  )}
                  {materiasError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {materiasError}
                    </div>
                  )}
                  {!materiaEsObligatoria && (
                    <div className="input-hint">
                      <i className="fas fa-circle-info"></i>
                      Para admins, el tipo institucional puede guardarse sin materia asociada.
                    </div>
                  )}
                  {materiaEsObligatoria && !materiasLoading && !materiasError && materias.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No tienes materias asignadas para asociar.
                    </div>
                  )}
                </div>

                {isAdminAreaUser && isInstitutionalTipoSeleccionado && !formData.idMateria && (
                  <div className="form-group">
                    <label htmlFor="idArea" className="form-label">
                      <i className="fas fa-sitemap"></i>
                      Área responsable *
                    </label>
                    <select
                      id="idArea"
                      className="form-select enhanced"
                      value={formData.idArea}
                      onChange={(e) => handleAreaChange(e.target.value)}
                      disabled={isSubmitting || areasLoading || availableAreas.length <= 1}
                      required
                    >
                      <option value="">Selecciona un área</option>
                      {availableAreas.map((area) => {
                        const id = area.id || area.Id;
                        const nombre = area.nombre || area.Nombre || 'Área';
                        return (
                          <option key={id} value={id}>
                            {nombre}
                          </option>
                        );
                      })}
                    </select>
                    <div className="input-hint">
                      <i className="fas fa-circle-info"></i>
                      La solicitud institucional del admin de área debe quedar asociada a un área aunque no tenga materia.
                    </div>
                    {areasLoading && (
                      <div className="input-hint">
                        <i className="fas fa-spinner fa-spin"></i>
                        Cargando áreas asignadas...
                      </div>
                    )}
                    {areasError && (
                      <div className="input-hint" style={{ color: '#dc2626' }}>
                        <i className="fas fa-triangle-exclamation"></i>
                        {areasError}
                      </div>
                    )}
                    {!areasLoading && !areasError && availableAreas.length === 0 && (
                      <div className="input-hint" style={{ color: '#b45309' }}>
                        <i className="fas fa-circle-info"></i>
                        No tienes áreas activas asignadas en este regional.
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="gptClasicoFraseIniciarConversacion" className="form-label">
                    <i className="fas fa-comments"></i>
                    Frases para iniciar una conversación
                  </label>
                  <textarea
                    id="gptClasicoFraseIniciarConversacion"
                    className="form-input enhanced"
                    rows="3"
                    value={formData.gptClasicoFraseIniciarConversacion}
                    onChange={(e) => handleInputChange('gptClasicoFraseIniciarConversacion', e.target.value)}
                    placeholder="Ej: Hola, ¿en qué tema necesitas ayuda?"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-eye"></i>
                    Visibilidad y acceso
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span>GPT público</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-sliders-h"></i>
                    Capacidades del GPT Clásico
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoBusquedaEnLaWeb}
                        onChange={(e) => handleInputChange('gptClasicoBusquedaEnLaWeb', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Búsqueda en la web</strong> — Permite consultar información externa al contenido cargado.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoLienzo}
                        onChange={(e) => handleInputChange('gptClasicoLienzo', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Lienzo</strong> — Habilita un espacio visual para organizar ideas y contenido.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoGeneracionDeImagen}
                        onChange={(e) => handleInputChange('gptClasicoGeneracionDeImagen', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Generación de imágenes</strong> — Permite crear imágenes a partir de descripciones.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoInterpreteCodigoYAnalisisDatos}
                        onChange={(e) => handleInputChange('gptClasicoInterpreteCodigoYAnalisisDatos', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Intérprete de código y análisis de datos</strong> — Ejecuta cálculos y análisis sobre archivos o datos.
                      </span>
                    </label>
                  </div>
                </div>

                {!formData.publico && (
                  <div className="form-group">
                    <label htmlFor="passwordAsistente" className="form-label">
                      <i className="fas fa-key"></i>
                      Password del GPT
                    </label>
                    <input
                      id="passwordAsistente"
                      type="text"
                      className="form-input enhanced"
                      value={formData.passwordAsistente}
                      onChange={(e) => handleInputChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>

              {/* Archivos PDF */}
              <div className="form-section files-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Documentos de Conocimiento</h3>
                    <p className="section-description">Sube los archivos PDF que contengan la información que tu asistente debe conocer</p>
                  </div>
                </div>
                
                <div className="files-upload-container">
                  <div 
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${formData.archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Los archivos se procesarán automáticamente para entrenar a tu asistente</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleFileInput}
                            disabled={isSubmitting}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Sin límite de tamaño configurado por la app</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {existingFiles.length > 0 && (
                  <div className="selected-files existing-files">
                    <h4>Archivos actuales ({existingFiles.length}):</h4>
                    <div className="files-list">
                      {existingFiles.map((file) => {
                        const fileId = file.id || file.Id;
                        const fileName = file.nombreArchivo || file.NombreArchivo || 'Archivo';
                        const fileUrl = file.urlPublica || file.UrlPublica || '';
                        return (
                          <div key={fileId || fileName} className="file-item">
                            <div className="file-info">
                              <i className="fas fa-file-pdf"></i>
                              <span className="file-name">{fileName}</span>
                            </div>
                            {fileUrl && (
                              <a className="download-file-btn" href={fileUrl} target="_blank" rel="noopener noreferrer">
                                <i className="fas fa-download"></i>
                                Descargar
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Lista de archivos seleccionados */}
                {formData.archivosPdf.length > 0 && (
                  <div className="selected-files">
                    <h4>Archivos seleccionados ({formData.archivosPdf.length}/20):</h4>
                    <div className="files-list">
                      {formData.archivosPdf.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <i className="fas fa-file-pdf"></i>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            disabled={isSubmitting}
                            title="Eliminar archivo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-section prompt-settings-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <i className="fas fa-scroll"></i>
                    </div>
                    <div className="section-text">
                      <h3 className="section-title">Instrucciones y aclaraciones</h3>
                      <p className="section-description">Completa las instrucciones específicas del GPT una vez definidos los archivos que va a usar.</p>
                    </div>
                  </div>

                  {isInstitutionalTipoSeleccionado && (
                    <div className="prompt-extra-field">
                      <label htmlFor="instruccionesInstitucionales" className="form-label">
                        <i className="fas fa-scroll"></i>
                        Instrucciones del asistente institucional *
                      </label>
                      <textarea
                        id="instruccionesInstitucionales"
                        className="form-input enhanced"
                        rows="8"
                        value={formData.instruccionesInstitucionales}
                        onChange={(e) => handleInputChange('instruccionesInstitucionales', e.target.value)}
                        placeholder="Describe el comportamiento, alcance y reglas que debe seguir este GPT institucional."
                        disabled={isSubmitting}
                      />
                      <div className="input-hint">
                        <i className="fas fa-info-circle"></i>
                        Este bloque se usará como cuerpo principal del prompt institucional, manteniendo la parte operativa obligatoria del sistema.
                      </div>
                    </div>
                  )}

                  <div className="prompt-extra-field">
                    <label htmlFor="promptExtra" className="form-label">
                      <i className="fas fa-pen"></i>
                      Aclaraciones extra (opcional)
                    </label>
                    <textarea
                      id="promptExtra"
                      className="form-input enhanced"
                      rows="6"
                      value={formData.promptExtra}
                      onChange={(e) => handleInputChange('promptExtra', e.target.value)}
                      placeholder="Ej: prioriza ejemplos resueltos, responde en español neutro..."
                      disabled={isSubmitting}
                    />
                    <div className="input-hint">
                      <i className="fas fa-info-circle"></i>
                      Estas reglas se agregarán al prompt base del asistente.
                    </div>
                  </div>
                </div>
              </div>

                </div>

                {!isInstitutionalTipoSeleccionado && (
                  <div className="assistant-prompt-panel">
                    <div className="prompt-panel-header">
                      <i className="fas fa-shield-alt"></i>
                      <div>
                        <h3>Prompt incluido</h3>
                        <p>Tu GPT Clásico incluirá automáticamente estas directivas</p>
                      </div>
                    </div>
                    <ul className="prompt-features-list">
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>RAG obligatorio</strong> — Usa exclusivamente los PDFs adjuntos como fuente de información</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Cita fuentes</strong> — Cita siempre el nombre del PDF y número de página</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>No inventa ni extrapola</strong> — Si no encuentra info en los PDFs, indica comunicarse con el profesor</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Tutoría académica</strong> — Puede armar planes de estudio y guías basadas en los PDFs</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Formato HTML</strong> — Todas las respuestas se entregan en HTML válido y renderizable</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Imágenes y esquemas</strong> — Incluye representaciones visuales fieles a los PDFs</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Genera archivos</strong> — Crea PDFs, Excel, Word y CSV cuando se solicitan</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Audio</strong> — Lee solo el texto visible, sin etiquetas HTML</span>
                      </li>
                      <li>
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Seguridad del prompt</strong> — No revela instrucciones internas al usuario</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Botones de Acción */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver al Dashboard
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || (!isEditMode && formData.archivosPdf.length === 0)}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {isEditMode ? 'Actualizando GPT Clásico...' : 'Creando GPT Clásico...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot"></i>
                      {isEditMode ? 'Actualizar GPT Clásico' : 'Crear GPT Clásico'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewGptAssistant;
